import { NoteLevelBadge } from '@/components/notes/note-level-badge';
import { ApiError } from '@/lib/api';
import {
  authorName,
  formatNoteDate,
  formatNoteDateTime,
  noteErrorMessage,
} from '@/lib/note-format';
import { notesKeys, useUpdateMemo } from '@/lib/notes-queries';
import { cn } from '@/lib/utils';
import {
  MEMO_CONFLICT,
  MEMO_MAX,
  type MatrixMemo,
  type MatrixNotesResponse,
  memoConflictDetailsSchema,
} from '@mss_creator/shared';
import { Button, Icon, TextArea } from '@pms-digital/design-system';
import { useQueryClient } from '@tanstack/react-query';
import { type KeyboardEvent, useState } from 'react';

const COUNTER_FROM = 3600;

export interface MemoEditorProps {
  matrixId: string;
  companyId: string;
  /** Named in the empty state and in « Visible par ». */
  companyName: string;
  /** The memo as `useMatrixNotes` returns it. */
  memo: MatrixMemo;
  /** The clock of the relative date; tests pin it. */
  now?: Date;
  className?: string;
}

/** Who wrote the version the caller did not see, from the `details` of a 409. */
function conflictAuthor(error: ApiError): string {
  const details = memoConflictDetailsSchema.safeParse(error.details);
  return details.success && details.data.updatedBy
    ? details.data.updatedBy.displayName
    : 'un autre utilisateur';
}

/**
 * The memo of a matrix (planches 12 and 16): one free text of level Client
 * that everybody edits in place with the pencil, « Modifié par X, <date> »
 * under it. Saving sends the `updatedAt` read when editing began; if someone
 * wrote in between, the server refuses (409) and the editor says who. The draft
 * stays on screen: « Recharger » fetches their version, shows it beside the
 * draft and moves the base to it, so saving again is a deliberate overwrite.
 */
export function MemoEditor({
  matrixId,
  companyId,
  companyName,
  memo,
  now,
  className,
}: MemoEditorProps) {
  const queryClient = useQueryClient();
  const updateMemo = useUpdateMemo(matrixId, companyId);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(memo.text);
  const [baseUpdatedAt, setBaseUpdatedAt] = useState<string | null>(memo.updatedAt);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<string | null>(null);
  /** The version someone else saved, once « Recharger » brought it in. */
  const [otherVersion, setOtherVersion] = useState<MatrixMemo | null>(null);
  const [reloading, setReloading] = useState(false);

  const saving = updateMemo.isPending;
  const overLimit = draft.length > MEMO_MAX;
  const canSave = !overLimit && !saving && conflict === null;
  const emptyPrompt = `Écrire un mémo pour ${companyName} : où en est la matrice, prochaines étapes…`;

  function startEditing() {
    // The version the user starts from: a refetch while typing must not move it.
    setBaseUpdatedAt(memo.updatedAt);
    setDraft(memo.text);
    setError(null);
    setConflict(null);
    setOtherVersion(null);
    setEditing(true);
  }

  function stopEditing() {
    setEditing(false);
    setError(null);
    setConflict(null);
    setOtherVersion(null);
  }

  async function save() {
    if (!canSave) return;
    if (draft.trim() === memo.text) {
      stopEditing();
      return;
    }
    setError(null);
    try {
      await updateMemo.mutateAsync({ text: draft.trim(), baseUpdatedAt });
      setEditing(false);
    } catch (failure) {
      if (failure instanceof ApiError && failure.code === MEMO_CONFLICT) {
        setConflict(conflictAuthor(failure));
      } else {
        setError(noteErrorMessage(failure, "Le mémo n'a pas pu être enregistré. Réessayez."));
      }
    }
  }

  async function reload() {
    setReloading(true);
    try {
      await queryClient.invalidateQueries({ queryKey: notesKeys.matrix(matrixId) });
      const latest = queryClient.getQueryData<MatrixNotesResponse>(
        notesKeys.matrix(matrixId),
      )?.memo;
      if (latest) {
        // The draft is kept; the next save is based on the version just read.
        setBaseUpdatedAt(latest.updatedAt);
        setOtherVersion(latest);
      }
      setConflict(null);
    } finally {
      setReloading(false);
    }
  }

  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      void save();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      stopEditing();
    }
  }

  const signature = memo.updatedAt && (
    <p className="mt-1 text-gray-500 text-micro">
      Modifié par {authorName(memo.updatedBy)},{' '}
      <time dateTime={memo.updatedAt} title={formatNoteDateTime(memo.updatedAt)}>
        {formatNoteDate(memo.updatedAt, now)}
      </time>
    </p>
  );

  return (
    <section aria-label="Mémo" className={cn('flex flex-col gap-2 text-caption', className)}>
      <div className="flex items-center gap-2">
        <h3 className="font-semibold text-gray-500 text-micro uppercase tracking-wider">Mémo</h3>
        <NoteLevelBadge visibility="client" />
        {!editing && (
          <button
            type="button"
            aria-label="Modifier le mémo"
            title="Modifier le mémo"
            onClick={startEditing}
            className="ml-auto inline-flex size-6 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
          >
            <Icon name="edit" size={14} />
          </button>
        )}
      </div>

      {editing ? (
        <div className="flex flex-col gap-2">
          <TextArea
            label={<span className="sr-only">Mémo</span>}
            autoFocus
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={emptyPrompt}
            status={overLimit ? 'error' : 'default'}
            readOnly={saving}
          />
          <div className="flex items-center gap-2">
            <span className="text-gray-600 text-micro">
              Visible par {companyName}. Ctrl+Entrée enregistre, Échap annule.
            </span>
            {draft.length >= COUNTER_FROM && (
              <span
                aria-live="polite"
                className={cn(
                  'text-micro',
                  overLimit ? 'font-semibold text-rouge-deep' : 'text-gray-600',
                )}
              >
                {draft.length} / {MEMO_MAX}
              </span>
            )}
            <div className="ml-auto flex items-center gap-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={stopEditing}
                disabled={saving}
              >
                Annuler
              </Button>
              <Button
                variant="secondary"
                type="button"
                size="sm"
                disabled={!canSave}
                onClick={() => void save()}
              >
                {saving ? 'Enregistrement…' : 'Enregistrer'}
              </Button>
            </div>
          </div>
          {otherVersion !== null && (
            <div className="flex flex-col gap-1 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-caption">
              <p className="font-semibold text-gray-600">
                Version enregistrée par {authorName(otherVersion.updatedBy)}. Votre texte est
                conservé : enregistrer le remplace.
              </p>
              <p className="whitespace-pre-wrap break-words">{otherVersion.text}</p>
            </div>
          )}
          {conflict !== null && (
            <div
              role="alert"
              className="flex items-center gap-2 rounded-md bg-warning-tint px-3 py-2 text-caption"
            >
              <span className="flex-1 font-semibold">
                Le mémo a été modifié par {conflict}. Rechargez pour voir sa version.
              </span>
              <Button
                type="button"
                variant="primary"
                disabled={reloading}
                onClick={() => void reload()}
              >
                {reloading ? 'Rechargement…' : 'Recharger'}
              </Button>
            </div>
          )}
          {error && (
            <p role="alert" className="font-semibold text-caption text-rouge-deep">
              {error}
            </p>
          )}
        </div>
      ) : (
        <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 leading-relaxed">
          {memo.text === '' ? (
            <button
              type="button"
              onClick={startEditing}
              className="rounded-sm text-left text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
            >
              {emptyPrompt}
            </button>
          ) : (
            <p className="whitespace-pre-wrap break-words">{memo.text}</p>
          )}
          {signature}
        </div>
      )}
    </section>
  );
}
